import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { ArrowLeft, ChevronRight, AlertTriangle, Bot, Mail, Ship } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import { useOrgPath } from '@/lib/pathFor'
import { formatDurationFromDates, formatShortDateTime } from '@/lib/formatters'
import { AGENT_SESSION_STATUS_COLORS } from '@/lib/constants'
import { capitalize } from '@/lib/utils'

interface EmailRef {
  id: string
  from: string | null
  subject: string | null
  date: string | null
}

interface ToolCall {
  id: string
  sequence_number: number | null
  tool_name: string
  arguments: Record<string, unknown> | null
  result: string | Record<string, unknown> | null
  duration_ms: number | null
  created_at: string
}

interface OutboundEmailRef {
  id: string
  to: string
  subject: string
  status: string
  reply_to_email_id: string | null
  created_at: string
  sent_at: string | null
}

interface SessionDetail {
  id: string
  email_thread_id: string | null
  thread_subject: string | null
  thread_status: string | null
  shipment_label: string | null
  shipment_count: number
  booking_id: string | null
  status: string
  escalation_reason: string | null
  model_used: string | null
  error_message: string | null
  started_at: string | null
  completed_at: string | null
  total_input_tokens: number | null
  total_output_tokens: number | null
  emails: EmailRef[]
  tool_calls: ToolCall[]
  outbound_emails: OutboundEmailRef[]
}

interface AgentSessionsShowProps {
  session: SessionDetail
}


export default function AgentSessionsShow({ session }: AgentSessionsShowProps) {
  const [expandedTool, setExpandedTool] = useState<string | null>(null)
  const canRetry = session.status === 'failed' || session.status === 'escalated'
  const pathFor = useOrgPath()

  function handleRetry() {
    if (confirm('Re-queue processing for this thread?')) {
      router.post(pathFor(`/agent_sessions/${session.id}/retry`))
    }
  }

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <div className="flex items-center gap-3">
          <Link href={pathFor('/agent_sessions')} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <span className="text-sm font-semibold font-mono">{session.id.slice(0, 8)}</span>
          <Badge variant="secondary" size="sm" className={`font-medium ${AGENT_SESSION_STATUS_COLORS[session.status] || ''}`}>
            {capitalize(session.status)}
          </Badge>
        </div>
        {canRetry && (
          <Button size="sm" onClick={handleRetry}>Retry</Button>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-8 space-y-6">
        {/* Thread Context */}
        <section className="rounded-lg border p-4">
          <h2 className="text-[13px] font-semibold text-foreground mb-3 flex items-center gap-2">
            <Bot className="h-4 w-4 text-primary" />
            Thread Context
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase mb-1">Thread</p>
              {session.email_thread_id ? (
                <Link
                  href={pathFor(`/inbox/threads/${session.email_thread_id}`)}
                  className="text-[13px] text-blue-600 hover:underline cursor-pointer"
                >
                  {session.thread_subject || '(no subject)'}
                </Link>
              ) : (
                <p className="text-[13px] text-muted-foreground">No thread linked</p>
              )}
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase mb-1">Thread Status</p>
              <p className="text-[13px] text-foreground">{capitalize(session.thread_status || 'unknown')}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase mb-1">Shipments</p>
              <p className="text-[13px] text-foreground">
                {session.shipment_count > 0 ? (
                  <span className="flex items-center gap-1">
                    <Ship className="h-3.5 w-3.5 text-muted-foreground" />
                    {session.shipment_count} linked
                    {session.shipment_label && (
                      <span className="text-muted-foreground"> ({session.shipment_label})</span>
                    )}
                  </span>
                ) : (
                  'None linked'
                )}
              </p>
            </div>
            {session.booking_id && (
              <div>
                <p className="text-[10px] text-muted-foreground uppercase mb-1">Primary Booking</p>
                <Link
                  href={pathFor(`/bookings/${session.booking_id}`)}
                  className="text-[13px] text-blue-600 hover:underline cursor-pointer"
                >
                  View Booking
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Error / Escalation Banner */}
        {session.error_message && (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
            <div>
              <p className="text-[13px] font-medium text-red-800">Session Failed</p>
              <p className="text-xs text-red-700 mt-1">{session.error_message}</p>
            </div>
          </div>
        )}

        {session.escalation_reason && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
            <div>
              <p className="text-[13px] font-medium text-amber-800">Escalated to Human</p>
              <p className="text-xs text-amber-700 mt-1">{session.escalation_reason}</p>
            </div>
          </div>
        )}

        {/* Metadata */}
        <section className="grid grid-cols-4 gap-4">
          <div className="rounded-md border p-3">
            <p className="text-[10px] text-muted-foreground uppercase">Model</p>
            <p className="text-[13px] font-medium mt-1">{session.model_used || '--'}</p>
          </div>
          <div className="rounded-md border p-3">
            <p className="text-[10px] text-muted-foreground uppercase">Duration</p>
            <p className="text-[13px] font-medium mt-1">{formatDurationFromDates(session.started_at, session.completed_at)}</p>
          </div>
          <div className="rounded-md border p-3">
            <p className="text-[10px] text-muted-foreground uppercase">Input Tokens</p>
            <p className="text-[13px] font-medium mt-1">{session.total_input_tokens?.toLocaleString() || '--'}</p>
          </div>
          <div className="rounded-md border p-3">
            <p className="text-[10px] text-muted-foreground uppercase">Output Tokens</p>
            <p className="text-[13px] font-medium mt-1">{session.total_output_tokens?.toLocaleString() || '--'}</p>
          </div>
        </section>

        {/* Emails Processed */}
        {session.emails.length > 0 && (
          <section>
            <h2 className="text-[13px] font-semibold text-foreground mb-2 flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              Emails Processed ({session.emails.length})
            </h2>
            <div className="space-y-1">
              {session.emails.map((email) => (
                <div
                  key={email.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2"
                >
                  <div>
                    <span className="text-[13px] text-foreground">{email.subject || 'No subject'}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{email.from}</span>
                  </div>
                  {email.date && (
                    <span className="text-[11px] text-muted-foreground">{formatShortDateTime(email.date)}</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Tool Call Trace */}
        <section>
          <h2 className="text-[13px] font-semibold text-foreground mb-2">
            Tool Calls ({session.tool_calls.length})
          </h2>
          <div className="rounded-lg border divide-y">
            {session.tool_calls.map((tc) => (
              <div key={tc.id}>
                <button
                  onClick={() => setExpandedTool(expandedTool === tc.id ? null : tc.id)}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-muted/30 cursor-pointer"
                >
                  <ChevronRight className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${expandedTool === tc.id ? 'rotate-90' : ''}`} />
                  <span className="text-[13px] font-mono text-foreground">{tc.tool_name}</span>
                  {tc.duration_ms != null && (
                    <span className="text-[10px] text-muted-foreground ml-auto">{tc.duration_ms}ms</span>
                  )}
                </button>
                {expandedTool === tc.id && (
                  <div className="px-4 pb-3 space-y-2">
                    {tc.arguments && (
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase mb-1">Arguments</p>
                        <pre className="text-xs bg-muted/50 rounded p-2 overflow-x-auto">
                          {JSON.stringify(tc.arguments, null, 2)}
                        </pre>
                      </div>
                    )}
                    {tc.result && (
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase mb-1">Result</p>
                        <pre className="text-xs bg-muted/50 rounded p-2 overflow-x-auto max-h-48">
                          {typeof tc.result === 'string' ? tc.result : JSON.stringify(tc.result, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {session.tool_calls.length === 0 && (
              <p className="px-4 py-4 text-xs text-muted-foreground">No tool calls</p>
            )}
          </div>
        </section>

        {/* Outbound Emails */}
        {session.outbound_emails.length > 0 && (
          <section>
            <h2 className="text-[13px] font-semibold text-foreground mb-2">Outbound Emails Created ({session.outbound_emails.length})</h2>
            <div className="space-y-1">
              {session.outbound_emails.map((email) => (
                <div
                  key={email.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2"
                >
                  <div>
                    <span className="text-[13px] text-foreground">{email.subject}</span>
                    <span className="ml-2 text-xs text-muted-foreground">To: {email.to}</span>
                  </div>
                  <Badge variant="secondary" size="sm" className="font-medium">
                    {capitalize(email.status)}
                  </Badge>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  )
}

AgentSessionsShow.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>{page}</AuthenticatedLayout>
)
